
import { ACTION_TYPES } from 'utils/constants/app_router';

function getWindow() {
    if (typeof window !== 'undefined') {
        return window;
    }
    return undefined;
}

/**
 * Sync vendor_id from actionData to localStorage (vendor_id === outlet_id)
 * Call this before any navigation to ensure the correct outlet context
 * @param {object} actionData - The payload data containing vendor_id
 */
function syncVendorIdToLocalStorage(actionData) {
    const vendorId = actionData?.vendor_id || actionData?.vendorId || actionData?.VendorId;
    if (vendorId && getWindow()) {
        window.localStorage.setItem('outlet_id', vendorId);
    }
}

function handlePoPendingAcceptanceDetail(actionData, navigate, otherData) {
    const purchaseOrderId = actionData?.purchase_order_id || actionData?.purchaseOrderId || actionData?.PurchaseOrderId;
    if (!purchaseOrderId) {
        console.warn('[handlePoPendingAcceptanceDetail] Missing purchaseOrderId');
        return false;
    }
    syncVendorIdToLocalStorage(actionData);
    navigate(`/po/details/pending/${purchaseOrderId}`);
    return true;
}

function handleDdPoListPage(actionData, navigate, otherData) {
    const purchaseOrderId = actionData?.purchase_order_id || actionData?.purchaseOrderId || actionData?.PurchaseOrderId;
    syncVendorIdToLocalStorage(actionData);
    if (purchaseOrderId) {
        navigate(`/ddPo/details/invoice/${purchaseOrderId}`);
    } else {
        navigate('/ddPo/all');
    }
    return true;
}

function handleAllPoNoGrnYetPage(actionData, navigate, otherData) {
    syncVendorIdToLocalStorage(actionData);
    // Navigate to /po/all with tab=2 (No GRN Yet tab)
    navigate('/po/all?tab=2');
    return true;
}

function handleGrnPendingConfirmationPage(actionData, navigate, otherData) {
    const purchaseOrderId = actionData?.purchase_order_id || actionData?.purchaseOrderId || actionData?.PurchaseOrderId;
    const poGrnMappingId = actionData?.po_grn_mapping_id || actionData?.poGrnMappingId || actionData?.PoGrnMappingId;
    
    syncVendorIdToLocalStorage(actionData);
    
    if (purchaseOrderId && poGrnMappingId) {
        navigate(`/grn/details/pending/${purchaseOrderId}/${poGrnMappingId}`);
    } else {
        navigate('/grn/pending');
    }
    return true;
}


/**
 * Handles action type based navigation/effects - used by JSBridge for native app communication
 * @param {string} actionType - The action type string from backend/native
 * @param {object} actionData - The payload data for the action
 * @param {function} navigate - react-router navigate function
 * @param {object} otherData - Additional handlers (modals, toasts, stores, etc.)
 * @returns {boolean} - true if action was handled, false otherwise
 */
export function handleActionType(actionType, actionData = {}, navigate, otherData = {}) {
    if (!actionType) {
        console.warn('[handleActionType] No actionType provided');
        return false;
    }

    switch (actionType) {
        case ACTION_TYPES.PO_PENDING_ACCEPTANCE_DETAIL:
            return handlePoPendingAcceptanceDetail(actionData, navigate, otherData);

        case ACTION_TYPES.DD_PO_LIST_PAGE:
            return handleDdPoListPage(actionData, navigate, otherData);

        case ACTION_TYPES.ALL_PO_NO_GRN_YET_PAGE:
            return handleAllPoNoGrnYetPage(actionData, navigate, otherData);

        case ACTION_TYPES.GRN_PENDING_CONFIRMATION_PAGE:
            return handleGrnPendingConfirmationPage(actionData, navigate, otherData);

        default:
            console.warn(`[handleActionType] Unknown actionType: ${actionType}`);
            if (navigate) {
                navigate('/');
            }
            return false;
    }
}

/**
 * Parse action payload from JSBridge (handles string, object, various casings)
 * @param {string|object} payload - The payload from JSBridge
 * @returns {object} Parsed payload with actionType and actionData
 */
export function parseActionPayload(payload) {
    if (!payload) return { actionType: null, actionData: {} };
    
    try {
        const parsed = typeof payload === 'string' ? JSON.parse(payload) : payload;
        return {
            actionType: parsed.actionType || parsed.ActionType || parsed.action_type,
            actionData: parsed.actionData || parsed.ActionData || parsed.action_data || {},
        };
    } catch (e) {
        console.error('[parseActionPayload] Failed to parse payload:', e);
        return { actionType: null, actionData: {} };
    }
}

/**
 * Setup JSBridge handler for action-based navigation
 * Call once at app init with navigate and any additional handlers
 * @param {function} navigate - react-router navigate function
 * @param {object} otherData - Additional handlers (showModal, showToast, etc.)
 */
export function setupJSBridgeActionHandler(navigate, otherData = {}) {
    if (!getWindow()) return;

    window.handleActionTypeFromNative = (payload) => {
        const { actionType, actionData } = parseActionPayload(payload);
        return handleActionType(actionType, actionData, navigate, otherData);
    };

    window.handleSellerHubAction = window.handleActionTypeFromNative;
}
